import { Brain, Eye, Wrench } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { LitellmModel } from './types'

function formatContext(tokens: number | null) {
  if (!tokens || tokens <= 0) return null
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(tokens % 1_000_000 === 0 ? 0 : 1)}M ctx`
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k ctx`
  return `${tokens} ctx`
}

function formatCostPair(input: number | null, output: number | null) {
  if (input == null && output == null) return null
  const fmt = (value: number | null) => {
    if (value == null) return '—'
    if (value >= 100) return `$${Math.round(value)}`
    if (value >= 10) return `$${value.toFixed(1)}`
    return `$${value.toFixed(2)}`
  }
  return `${fmt(input)} / ${fmt(output)} /1M`
}

export function ModelMetaBadges({ model, compact = false }: { model: LitellmModel; compact?: boolean }) {
  const context = formatContext(model.max_input_tokens)
  const cost = formatCostPair(model.input_cost_per_million, model.output_cost_per_million)

  return (
    <div className={`flex flex-wrap items-center ${compact ? 'gap-1' : 'gap-1.5'}`}>
      {model.mode && model.mode !== 'chat' && (
        <Badge variant="purple" className="font-normal">{model.mode}</Badge>
      )}
      {context && <Badge variant="info" className="font-normal">{context}</Badge>}
      {cost && <Badge variant="info" className="font-normal font-mono">{cost}</Badge>}
      {model.supports_function_calling && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="success" className="px-1.5">
              <Wrench className="h-3 w-3" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>Suporta function calling / tools</TooltipContent>
        </Tooltip>
      )}
      {model.supports_vision && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="success" className="px-1.5">
              <Eye className="h-3 w-3" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>Suporta imagens (vision)</TooltipContent>
        </Tooltip>
      )}
      {!context && !cost && !model.mode && (
        <Badge variant="outline" className="font-normal text-muted-foreground">
          <Brain className="h-3 w-3" />
          sem metadata
        </Badge>
      )}
    </div>
  )
}

export function ModelProviderTag({ model }: { model: LitellmModel }) {
  if (!model.provider) return null
  return (
    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {model.provider}
    </span>
  )
}
